import LocalizedLink from '@/components/layout/LocalizedLink'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { BookOpen } from 'lucide-react'
import { LocalizedString, getLocalizedValue } from '@sakeena/shared'
import { useTranslation } from 'react-i18next'

export interface Book {
  id: string
  title: LocalizedString | string
  author: string
  description?: LocalizedString | string
  coverImageUrl?: string
  category?: string
  language?: string
}

interface BookCardProps {
  book: Book
}

export default function BookCard({ book }: BookCardProps) {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language || 'ar'

  return (
    <Card className="h-full flex flex-col overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
        <img
          src={book.coverImageUrl || '/placeholders/book-cover.jpg'}
          alt={getLocalizedValue(book.title, currentLang)}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {book.category && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-primary-600/90 text-white text-[10px] rounded uppercase tracking-wider font-bold">
            {book.category}
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-bold text-gray-900 dark:text-gray-100 line-clamp-1 mb-1">
          {getLocalizedValue(book.title, currentLang)}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          {book.author}
        </p>
        {book.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed mb-4">
            {getLocalizedValue(book.description, currentLang)}
          </p>
        )}
        <div className="mt-auto">
          <LocalizedLink to={`/library/${book.id}`}>
            <Button variant="outline" className="w-full text-xs py-2 gap-2">
              <BookOpen className="w-3 h-3" />
              {t('common.readBook', 'قراءة الكتاب')}
            </Button>
          </LocalizedLink>
        </div>
      </div>
    </Card>
  )
}
